import React, { useState, useEffect, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { formatDateLong } from "../../../../../utils/textUtils";
import SectionBadge from "../../EmployeeTransactions/ui/SectionBadge";
import EmployeeCard from "../../../ui/EmployeeCard";
import { useLoanTransaction } from "../../../../hooks/useLoanTransaction";
import loadingSpinner from "../../../../../assets/loading.gif";
import { Input } from "../../../../../common/Input";
import { LOAN_TYPES } from "../../../../../utils/enums";
import { StatusBadge } from "../../../../../common/Badge";


const LoanLogsModal = ({ isOpen, onClose, employee, showGeneralMessage }) => {
  const { fetchEmployeeLoanLogs } = useLoanTransaction(showGeneralMessage);

  const [loanType, setLoanType] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async (type) => {
    if (!type || !employee?.id) return;
    setLoading(true);
    try {
      const response = await fetchEmployeeLoanLogs(type, employee.id);
      setLogs(response?.data || []);
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      setLoading(false);
    }
  }, [employee?.id, fetchEmployeeLoanLogs]);

  useEffect(() => {
    if (loanType) {
      fetchLogs(loanType);
    } else {
      setLogs([]);
    }
  }, [loanType, fetchLogs]);

  const handleClose = () => {
    setLoanType("");
    setLogs([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700">
      <div className="bg-white w-full md:w-[70%] lg:w-[60%] rounded-lg shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50 rounded-t-lg">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-blue-900 uppercase tracking-tight">
              Employee Loan Logs
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer">
            <CloseIcon />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* Section 1: Selection */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
            <SectionBadge label="Target Employee" editable={false} />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Employee ID" value={employee?.employeeId ?? ""} isViewOnly={true} className="bg-white" />
              <div className="flex flex-col">
                <label className="text-xs font-bold text-gray-500 mb-2 uppercase">Loan Type:</label>
                <select
                  value={loanType ?? ""}
                  onChange={(e) => {
                    setLoanType(e.target.value);
                  }}
                  className="border border-gray-300 rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">-- Select Loan Type --</option>
                  {LOAN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <EmployeeCard data={employee ? { fullName: employee.fullName, employeeId: employee.employeeId, position: employee.positionDescription } : null} />
          </div>

          {/* Section 2: Logs Table */}
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm flex flex-col min-h-[300px]">
            <div className="bg-gray-100 px-4 py-2 border-b">
              <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Transaction History</span>
            </div>

            <div className="flex-1 overflow-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-full py-10">
                  <img src={loadingSpinner} alt="Loading..." className="w-12 h-12" />
                  <p className="text-xs text-gray-400 font-bold uppercase mt-2">Fetching Logs...</p>
                </div>
              ) : logs.length > 0 ? (
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 bg-blue-900 text-white text-[10px] uppercase font-bold">
                    <tr>
                      <th className="px-4 py-3">Processed Date</th>
                      <th className="px-4 py-3">Employee ID</th>
                      <th className="px-4 py-3">Employee Name</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-center">Status</th>
                      <th className="px-4 py-3">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs divide-y divide-gray-100">
                    {logs.map((log, index) => (
                      <tr key={index} className="hover:bg-blue-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-600">
                          {log.processedDate ? formatDateLong(log.processedDate) : "---"}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{log.employeePersonalInformationEmployeeId}</td>
                        <td className="px-4 py-3 font-bold text-gray-700">{log.employeePersonalInformationFullName}</td>
                        <td className="px-4 py-3 text-right font-bold text-blue-700">
                          ₱{log.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {log.status ? <StatusBadge status={log.status} /> : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-gray-500 italic max-w-xs truncate" title={log.remarks}>
                          {log.remarks || "---"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-20 text-gray-400">
                  <ListAltIcon sx={{ fontSize: 40, opacity: 0.2 }} />
                  <p className="text-sm font-medium">{loanType ? "No logs found for this type." : "Select a loan type to view history."}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={handleClose}
            className="px-8 py-2 bg-gray-400 text-white rounded font-bold border-none cursor-pointer hover:bg-gray-500 transition-colors uppercase text-xs"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoanLogsModal;